import { useEffect, useRef, useState } from 'react';
import { Store } from 'components/common/types/Store.types';
import { useDispatch, useSelector } from 'react-redux';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { changeDirectSalesData } from 'store/dealManagement/reducer';
import { addToastNotification } from 'store/notification/reducer';
import { getCampaignPlanConfig, saveCampaignPlanConfig } from 'modules/api/campaignPlan';
import { cancelFunctions } from 'utils/cancelFunctions';
import {
  CampaignPlanAllocationMetricId,
  CampaignPlanAllocationReportDealLineSelectConfig,
  CampaignPlanConfig,
  CampaignPlanConfigResponse,
  CampaignPlanShareConfig,
} from 'components/common/types/CampaignPlan.types';
import { createCampaignPlanUrlFromToken } from 'components/common/Deal/CampaignHeader/CampaignDrawer/ShareCampaignPlan/createCampaignPlanUrlFromToken';
import { createShareCampaignPlanToken } from 'modules/api/shareCampaignPlan';
import { getAllocationReportDealLineSelect, getMarketId } from 'store/publisher/selectors';
import {
  ALLOCATION_METRICS,
  CAMPAIGN_PLAN_CONFIG_LOAD_ERROR,
  GROUP_BY_OPTION_CODES,
  ShareCampaignPlanProps,
  ToggleCampaignPlanProps,
} from './ShareCampaignPlanModal.types';
import { copyToClipboard } from '../../../../utils/copyToClipboard';

type SetParams<T> = {
  setParams: (params: Partial<T>) => void;
};

const createInitialCampaignPlanConfig = (
  dealLineSelect: CampaignPlanAllocationReportDealLineSelectConfig,
): CampaignPlanConfig => {
  const metrics = ALLOCATION_METRICS.reduce<Record<CampaignPlanAllocationMetricId, { planned: boolean }>>(
    (acc, { id }) => {
      acc[id] = { planned: id === 'sot' };
      return acc;
    },
    {} as Record<CampaignPlanAllocationMetricId, { planned: boolean }>,
  );

  return {
    allocationReport: {
      ...metrics,
      allowedGroupByOptions: [...GROUP_BY_OPTION_CODES],
      dealLineSelect,
    },
    map: {
      includeMap: true,
      includePOIs: false,
    },
  };
};

const applyPublisherDealLineSelectToConfig = (
  config: CampaignPlanConfig,
  dealLineSelect: CampaignPlanAllocationReportDealLineSelectConfig,
): CampaignPlanConfig => ({
  ...config,
  allocationReport: {
    ...config.allocationReport,
    dealLineSelect,
  },
});

const toShareLink = (share?: CampaignPlanShareConfig): { url: string; expiresAt: string } | undefined => {
  if (!share?.token || !share.expiresAt) return undefined;

  const expiryTime = new Date(share.expiresAt).getTime();

  if (Number.isNaN(expiryTime) || expiryTime <= Date.now()) return undefined;

  return { url: createCampaignPlanUrlFromToken(share.token), expiresAt: share.expiresAt };
};

interface UseShareCampaignPlanModal {
  modalIsOpen: boolean;
  shareCampaignPlan: ShareCampaignPlanProps & SetParams<ShareCampaignPlanProps>;
  toggleCampaignPlan: ToggleCampaignPlanProps & SetParams<ToggleCampaignPlanProps>;
  campaignPlanConfig: CampaignPlanConfig;
  isConfigLoading: boolean;
  configLoadError: string;
  setIsOpen: (isOpen: boolean) => void;
  generateCampaignPlanLink: (campaignPlanConfig: CampaignPlanConfig) => Promise<void>;
  updateCampaignPlanConfig: (campaignPlanConfig: CampaignPlanConfig, hasConfigChanges: boolean) => Promise<boolean>;
}

export const useShareCampaignPlanModal = (): UseShareCampaignPlanModal => {
  const dispatch = useDispatch();
  const publisherId = useSelector(getMarketId);
  const dealLineSelect = useSelector(getAllocationReportDealLineSelect);
  const campaignId = useSelector(getDealId);
  const campaignPlanShareUrl = useSelector((state: Store) => state.dealManagement.directSales.campaignPlanShareUrl);
  const campaignPlanShareExpiresAt = useSelector(
    (state: Store) => state.dealManagement.directSales.campaignPlanShareExpiresAt,
  );
  const isGenerateCampaignPlanEnabled = useSelector(
    (state: Store) => state.dealManagement.directSales.isGenerateCampaignPlanEnabled,
  );
  const isGenerateCampaignPlanEnabledRef = useRef(isGenerateCampaignPlanEnabled);
  isGenerateCampaignPlanEnabledRef.current = isGenerateCampaignPlanEnabled;
  const enabledForSharing = useSelector((state: Store) => state.dealManagement.directSales.enabledForSharing);
  const enabledForSharingRef = useRef(enabledForSharing);
  enabledForSharingRef.current = enabledForSharing;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [campaignPlanConfig, setCampaignPlanConfig] = useState(() => createInitialCampaignPlanConfig(dealLineSelect));
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [configLoadError, setConfigLoadError] = useState('');
  const [hasPersistedCampaignPlanConfig, setHasPersistedCampaignPlanConfig] = useState(false);
  const [shareCampaignPlan, setShareCampaignPlan] = useState<ShareCampaignPlanProps>({
    isLoading: false,
    url: '',
    error: '',
    expiresAt: '',
    disabledMessage: '',
  });
  const [toggleCampaignPlan, setToggleCampaignPlan] = useState<ToggleCampaignPlanProps>({
    isChecked: false,
    isDisabled: false,
    error: '',
  });

  const setShareCampaignPlanParams = (params: Partial<ShareCampaignPlanProps>): void => {
    setShareCampaignPlan((prev: ShareCampaignPlanProps) => ({
      ...prev,
      ...params,
    }));
  };

  const setToggleCampaignPlanParams = (params: Partial<ToggleCampaignPlanProps>): void => {
    setToggleCampaignPlan((prev: ToggleCampaignPlanProps) => ({
      ...prev,
      ...params,
    }));
  };

  const persistShareLink = (url: string, expiresAt: string): void => {
    setShareCampaignPlanParams({ url, expiresAt, error: '' });
    dispatch(changeDirectSalesData({ campaignPlanShareUrl: url, campaignPlanShareExpiresAt: expiresAt }));
  };

  const setIsOpen = (isOpen: boolean): void => {
    if (isOpen) {
      setIsConfigLoading(true);
    }

    setModalIsOpen(isOpen);
  };

  const restoreShareLink = (share?: CampaignPlanShareConfig): void => {
    if (enabledForSharingRef.current === false) {
      setShareCampaignPlanParams({ url: '', expiresAt: '', error: '' });
      return;
    }

    const shareLink = toShareLink(share);

    if (shareLink) {
      dispatch(changeDirectSalesData({ isGenerateCampaignPlanEnabled: false }));
      persistShareLink(shareLink.url, shareLink.expiresAt);
      return;
    }

    if (isGenerateCampaignPlanEnabledRef.current) {
      setShareCampaignPlanParams({ url: '', expiresAt: '', error: '' });
      return;
    }

    if (campaignPlanShareUrl) {
      setShareCampaignPlanParams({ url: campaignPlanShareUrl, expiresAt: campaignPlanShareExpiresAt ?? '' });
    }
  };

  const applyLoadedConfig = (savedConfig?: CampaignPlanConfigResponse): void => {
    const initialConfig = createInitialCampaignPlanConfig(dealLineSelect);

    setCampaignPlanConfig({
      allocationReport: savedConfig?.allocationReport ?? initialConfig.allocationReport,
      map: savedConfig?.map ?? initialConfig.map,
      ...(savedConfig?.share && { share: savedConfig.share }),
    });
    setHasPersistedCampaignPlanConfig(Boolean(savedConfig?.allocationReport));
    restoreShareLink(savedConfig?.share);
  };

  useEffect(() => {
    if (!modalIsOpen) return;

    if (isGenerateCampaignPlanEnabled || enabledForSharing === false || !campaignPlanShareUrl) {
      setShareCampaignPlanParams({ url: '', expiresAt: '', error: '' });
      return;
    }

    setShareCampaignPlan((prev) => {
      if (prev.isLoading) return prev;

      return {
        ...prev,
        url: campaignPlanShareUrl,
        expiresAt: campaignPlanShareExpiresAt ?? '',
      };
    });
  }, [modalIsOpen, isGenerateCampaignPlanEnabled, enabledForSharing, campaignPlanShareUrl, campaignPlanShareExpiresAt]);

  useEffect(() => {
    if (!modalIsOpen) return;

    const loadModalData = async (): Promise<void> => {
      setIsConfigLoading(true);
      setConfigLoadError('');

      if (enabledForSharing === false || isGenerateCampaignPlanEnabled || !campaignPlanShareUrl) {
        setShareCampaignPlanParams({ url: '', expiresAt: '', error: '' });
      } else {
        setShareCampaignPlanParams({
          url: campaignPlanShareUrl,
          expiresAt: campaignPlanShareExpiresAt ?? '',
        });
      }

      try {
        applyLoadedConfig(await getCampaignPlanConfig(campaignId, publisherId, cancelFunctions));
      } catch {
        applyLoadedConfig(undefined);
        setConfigLoadError(CAMPAIGN_PLAN_CONFIG_LOAD_ERROR);
      } finally {
        setIsConfigLoading(false);
      }
    };

    loadModalData();
  }, [modalIsOpen, campaignId, publisherId, dealLineSelect]);

  const updateCampaignPlanConfig = async (config: CampaignPlanConfig, hasConfigChanges: boolean): Promise<boolean> => {
    if (!hasConfigChanges || !campaignPlanShareUrl) return true;

    try {
      await saveCampaignPlanConfig(campaignId, publisherId, config, cancelFunctions);

      setCampaignPlanConfig(config);
      setHasPersistedCampaignPlanConfig(true);
      await copyToClipboard(campaignPlanShareUrl);

      dispatch(
        addToastNotification({
          type: 'Success',
          header: 'Campaign plan link updated and copied to clipboard.',
          timeout: 2000,
        }),
      );

      return true;
    } catch (error) {
      setShareCampaignPlanParams({
        error: 'Campaign plan config could not be saved.',
      });

      return false;
    }
  };

  const generateCampaignPlanLink = async (config: CampaignPlanConfig): Promise<void> => {
    setShareCampaignPlanParams({ isLoading: true, url: '', error: '', expiresAt: '', disabledMessage: '' });
    setToggleCampaignPlanParams({ error: '' });

    try {
      const { token, expiresAt } = await createShareCampaignPlanToken(cancelFunctions, publisherId, campaignId);
      const configToSave: CampaignPlanConfig = {
        ...(hasPersistedCampaignPlanConfig ? config : applyPublisherDealLineSelectToConfig(config, dealLineSelect)),
        share: { token, expiresAt },
      };

      await saveCampaignPlanConfig(campaignId, publisherId, configToSave, cancelFunctions);

      setCampaignPlanConfig(configToSave);
      setHasPersistedCampaignPlanConfig(true);

      const url = createCampaignPlanUrlFromToken(token);
      persistShareLink(url, expiresAt);
      dispatch(changeDirectSalesData({ isGenerateCampaignPlanEnabled: false }));

      const copyError = await copyToClipboard(url);

      dispatch(
        addToastNotification({
          type: 'Success',
          header: copyError ? 'Campaign plan link generated.' : 'Campaign plan link generated and copied to clipboard.',
          timeout: 2000,
        }),
      );
    } catch {
      setShareCampaignPlanParams({ error: 'Link could not be generated.' });
    } finally {
      setShareCampaignPlanParams({ isLoading: false });
    }
  };

  return {
    modalIsOpen,
    setIsOpen,
    campaignPlanConfig,
    isConfigLoading,
    configLoadError,
    shareCampaignPlan: {
      ...shareCampaignPlan,
      setParams: setShareCampaignPlanParams,
    },
    toggleCampaignPlan: {
      ...toggleCampaignPlan,
      setParams: setToggleCampaignPlanParams,
    },
    generateCampaignPlanLink,
    updateCampaignPlanConfig,
  };
};
